// Health Screening Recommendations — Cardiometabolic Risk Assessment
// Sources: AHA, ADA, ACC/AHA, USPSTF, CDC
//
// ─── CITATIONS ───
// [1] ADA Standards of Care 2024 — Diabetes screening: age ≥35 or overweight + risk factors
// [2] ACC/AHA 2019 Guideline — Cholesterol management, ASCVD risk assessment
// [3] AHA 2023 — Updated hypertension guidelines (130/80 threshold)
// [4] USPSTF 2021 — Screening for prediabetes/type 2 diabetes (Grade B, adults 35-70 overweight/obese)
// [5] CDC 2024 — National Diabetes Statistics Report
// [6] WHO 2023 — Global report on hypertension

/**
 * Generate cardiometabolic screening recommendations based on survey data.
 * Returns an array of recommendation objects for display on the results page.
 */
export function getHealthScreeningRecommendations(surveyData) {
  const {
    age = 0,
    biologicalSex = "",
    bloodSugarStatus = "",
    cholesterolStatus = "",
    bloodPressureStatus = "",
    chronicDiseases = [],
    ethnicity = "",
    weight = 0,
    height = 0,
    smokingHistory = 0,
    cigarettesPerDay = 0,
    alcoholConsumption = "",
    familyCancerHistory = [],
  } = surveyData;

  const recommendations = [];
  const sex = (biologicalSex || "").toLowerCase();
  // Normalize new NIH/CDC ethnicity values to internal risk keys
  const ethnicityMap = {
    "white": "caucasian",
    "black": "african",
    "native_american": "african",
    "pacific_islander": "asian",
    "prefer_not_to_say": "",
  };
  const rawEth = (ethnicity || "").toLowerCase();
  const ethLower = ethnicityMap[rawEth] || rawEth;
  const hasDiabetes = chronicDiseases.includes("diabetes") || bloodSugarStatus === "diabetic";
  const hasHypertension = bloodPressureStatus === "high";
  const hasHighCholesterol = cholesterolStatus === "high";
  const isOverweight = (() => {
    const weightKg = surveyData.weightInKg || weight;
    const heightCm = surveyData.heightInCm || height;
    if (!weightKg || !heightCm) return false;
    const bmi = weightKg / ((heightCm / 100) ** 2);
    return bmi >= 25;
  })();
  const smokes = (parseFloat(smokingHistory) || 0) * (parseFloat(cigarettesPerDay) || 0) > 0;

  // ─── BLOOD SUGAR / DIABETES SCREENING ───
  const diabetesRiskFactors = [];
  let diabetesRiskLevel = "low";

  if (bloodSugarStatus === "diabetic") {
    diabetesRiskLevel = "diagnosed";
    diabetesRiskFactors.push("Diagnosed with diabetes");
  } else if (bloodSugarStatus === "prediabetic") {
    diabetesRiskLevel = "high";
    diabetesRiskFactors.push("Pre-diabetic blood sugar levels");
  }

  if (age >= 45) diabetesRiskFactors.push("Age 45+");
  if (isOverweight) diabetesRiskFactors.push("Overweight/obese (BMI ≥ 25)");
  if (["asian", "african", "hispanic"].includes(ethLower)) {
    diabetesRiskFactors.push("Higher-risk ethnicity (screening recommended at BMI ≥ 23 for Asian populations)");
  }
  if (familyCancerHistory.some(f => f.toLowerCase().includes("diabetes"))) {
    diabetesRiskFactors.push("Family history of diabetes");
  }
  if (hasHypertension) diabetesRiskFactors.push("High blood pressure");
  if (hasHighCholesterol) diabetesRiskFactors.push("High cholesterol");

  if (diabetesRiskLevel === "low" && diabetesRiskFactors.length >= 2) {
    diabetesRiskLevel = "moderate";
  }
  if (diabetesRiskLevel === "low" && (age >= 35 && isOverweight)) {
    diabetesRiskLevel = "moderate"; // USPSTF Grade B
  }

  recommendations.push({
    id: "bloodSugar",
    category: "Blood Sugar & Diabetes",
    icon: "🩸",
    riskLevel: diabetesRiskLevel,
    status: bloodSugarStatus || "unsure",
    riskFactors: diabetesRiskFactors,
    screening: {
      method: "Fasting plasma glucose (FPG) or HbA1c blood test",
      guideline: "USPSTF Grade B; ADA Standards of Care 2024",
      who: "Adults 35–70 who are overweight/obese; earlier if risk factors present",
      interval: diabetesRiskLevel === "diagnosed"
        ? "HbA1c every 3 months; annual comprehensive metabolic panel"
        : diabetesRiskLevel === "high"
        ? "Retest every 6–12 months; consider oral glucose tolerance test (OGTT)"
        : "Every 3 years if normal; annually if risk factors present",
      source: "USPSTF 2021; ADA 2024; CDC",
    },
    actionItems: diabetesRiskLevel === "diagnosed"
      ? [
          "Monitor blood sugar daily or as directed by your doctor",
          "Maintain HbA1c below 7% (or your doctor's target)",
          "Annual eye exam, foot exam, and kidney function test",
          "Heart-healthy diet low in refined carbs and added sugars",
          "150+ minutes/week of moderate physical activity",
        ]
      : diabetesRiskLevel === "high"
      ? [
          "Reduce refined carbohydrate and sugar intake",
          "Aim for 5–7% body weight loss if overweight",
          "150+ minutes/week of moderate exercise (brisk walking, cycling)",
          "Ask your doctor about metformin for diabetes prevention",
          "Retest fasting glucose or HbA1c in 6–12 months",
        ]
      : [
          "Maintain a balanced diet rich in whole grains, vegetables, and lean protein",
          "Stay physically active — 150+ minutes/week of moderate exercise",
          "Know your numbers: get a fasting glucose test at your next checkup",
          "Limit sugary drinks and processed foods",
        ],
  });

  // ─── CHOLESTEROL SCREENING ───
  const cholesterolRiskFactors = [];
  let cholesterolRiskLevel = "low";

  if (cholesterolStatus === "high") {
    cholesterolRiskLevel = "high";
    cholesterolRiskFactors.push("High cholesterol (≥ 240 mg/dL or diagnosed)");
  } else if (cholesterolStatus === "borderline") {
    cholesterolRiskLevel = "moderate";
    cholesterolRiskFactors.push("Borderline high cholesterol (200–239 mg/dL)");
  }

  if (age >= 40) cholesterolRiskFactors.push("Age 40+");
  if (smokes) cholesterolRiskFactors.push("Smoking history");
  if (hasDiabetes) cholesterolRiskFactors.push("Diabetes");
  if (hasHypertension) cholesterolRiskFactors.push("High blood pressure");
  if (isOverweight) cholesterolRiskFactors.push("Overweight/obese");
  if (sex === "male" && age >= 45) cholesterolRiskFactors.push("Male age 45+");
  if (sex === "female" && age >= 55) cholesterolRiskFactors.push("Female age 55+");

  if (cholesterolRiskLevel === "low" && cholesterolRiskFactors.length >= 2) {
    cholesterolRiskLevel = "moderate";
  }

  recommendations.push({
    id: "cholesterol",
    category: "Cholesterol",
    icon: "🫀",
    riskLevel: cholesterolRiskLevel,
    status: cholesterolStatus || "unsure",
    riskFactors: cholesterolRiskFactors,
    screening: {
      method: "Lipid panel blood test (total cholesterol, LDL, HDL, triglycerides)",
      guideline: "ACC/AHA 2019; USPSTF Grade B (adults 40-75)",
      who: "Adults 20+ (baseline); regularly for ages 40-75 or with risk factors",
      interval: cholesterolRiskLevel === "high"
        ? "Every 6–12 months to monitor treatment response"
        : cholesterolRiskLevel === "moderate"
        ? "Annually"
        : "Every 4–6 years if normal; more often with risk factors",
      source: "ACC/AHA 2019; USPSTF 2023",
    },
    actionItems: cholesterolRiskLevel === "high"
      ? [
          "Discuss statin therapy with your doctor if not already on one",
          "Reduce saturated fat intake (red meat, full-fat dairy, fried foods)",
          "Increase soluble fiber (oats, beans, fruits, vegetables)",
          "Exercise at least 150 minutes/week — even walking helps",
          "Consider omega-3 supplements or fish oil (consult your doctor)",
        ]
      : cholesterolRiskLevel === "moderate"
      ? [
          "Adopt a heart-healthy diet (Mediterranean or DASH diet)",
          "Increase physical activity — aim for 30 min/day most days",
          "Limit alcohol to moderate levels",
          "Get a follow-up lipid panel in 6–12 months",
        ]
      : [
          "Maintain a heart-healthy diet with healthy fats (olive oil, nuts, avocado)",
          "Stay active — regular exercise raises HDL (good cholesterol)",
          "Get a baseline lipid panel if you haven't had one",
          "Limit trans fats and highly processed foods",
        ],
  });

  // ─── BLOOD PRESSURE SCREENING ───
  const bpRiskFactors = [];
  let bpRiskLevel = "low";

  if (bloodPressureStatus === "high") {
    bpRiskLevel = "high";
    bpRiskFactors.push("High blood pressure (≥ 140/90 mmHg or diagnosed)");
  } else if (bloodPressureStatus === "elevated") {
    bpRiskLevel = "moderate";
    bpRiskFactors.push("Elevated blood pressure (120–139/80–89 mmHg)");
  }

  if (age >= 45) bpRiskFactors.push("Age 45+");
  if (["african"].includes(ethLower)) {
    bpRiskFactors.push("African American ethnicity (higher prevalence)");
  }
  if (isOverweight) bpRiskFactors.push("Overweight/obese");
  if (smokes) bpRiskFactors.push("Smoking history");
  if (hasDiabetes) bpRiskFactors.push("Diabetes");
  if (hasHighCholesterol) bpRiskFactors.push("High cholesterol");
  if (alcoholConsumption && !["none", "under_7"].includes(alcoholConsumption)) {
    bpRiskFactors.push("Moderate-to-heavy alcohol consumption");
  }

  if (bpRiskLevel === "low" && bpRiskFactors.length >= 2) {
    bpRiskLevel = "moderate";
  }

  recommendations.push({
    id: "bloodPressure",
    category: "Blood Pressure",
    icon: "💓",
    riskLevel: bpRiskLevel,
    status: bloodPressureStatus || "unsure",
    riskFactors: bpRiskFactors,
    screening: {
      method: "Blood pressure measurement (sphygmomanometer or automated cuff)",
      guideline: "AHA/ACC 2023; USPSTF Grade A (adults 18+)",
      who: "All adults 18+; more frequently with risk factors",
      interval: bpRiskLevel === "high"
        ? "Monitor at home regularly; doctor visits every 1–3 months until controlled"
        : bpRiskLevel === "moderate"
        ? "Check every 3–6 months; consider home monitoring"
        : "At least annually at routine checkup",
      source: "AHA 2023; USPSTF 2021",
    },
    actionItems: bpRiskLevel === "high"
      ? [
          "Take prescribed medications as directed — don't skip doses",
          "Reduce sodium intake to < 2,300 mg/day (ideally < 1,500 mg)",
          "Follow the DASH diet (fruits, vegetables, whole grains, low-fat dairy)",
          "Exercise regularly — even 30 min/day of brisk walking helps",
          "Monitor blood pressure at home and keep a log for your doctor",
          "Manage stress through meditation, deep breathing, or yoga",
        ]
      : bpRiskLevel === "moderate"
      ? [
          "Reduce sodium intake — avoid processed and restaurant foods",
          "Increase potassium-rich foods (bananas, spinach, sweet potatoes)",
          "Get at least 150 minutes/week of aerobic exercise",
          "Limit alcohol to ≤ 1 drink/day (women) or ≤ 2 drinks/day (men)",
          "Maintain a healthy weight — even 5-10 lbs loss can help",
        ]
      : [
          "Keep sodium intake moderate (< 2,300 mg/day)",
          "Stay active with regular physical activity",
          "Get your blood pressure checked at your next doctor visit",
          "Maintain a healthy weight and manage stress",
        ],
  });

  return recommendations;
}
